import { ld } from "@/lib/theme/light-dark";
import {
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronLeft,
  Ellipsis,
  FileText,
  Folder,
  Inbox as InboxIcon,
  LayoutGrid,
  Menu,
  MessagesSquare,
  Paperclip,
  Phone,
  Plus,
  SquareCheckBig,
  SquarePen,
  Users,
  Video,
} from "lucide-react";
/**
 * The three phone-screen interiors for the Mobilversion hero module — a 1:1 port
 * of the prototype's `isMobil` block (Hausverwaltung Hero.dc.html, lines
 * 96–256). Each function renders the screen content that sits *inside* the
 * `IPhoneFrame` primitive (which supplies the device shell, Dynamic Island, iOS
 * status bar and home indicator). Phone 1 = Inbox list, phone 2 = Dokumente
 * grid, phone 3 = Team-Chat.
 */
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H } from "../../hero-tokens";
import {
  type MobilChatEntry,
  type MobilDocCard,
  type MobilInboxRow,
  mobilChat,
  mobilDocs,
  mobilInbox,
} from "./module-mobil-data";

const TAB_INACTIVE = ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)");

/** One bottom-tab item (icon + label). */
function TabItem(props: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly active?: boolean;
}): ReactNode {
  return (
    <span
      style={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        color: props.active ? H.accent : TAB_INACTIVE,
      }}
    >
      {props.icon}
      <span
        style={{
          width: "100%",
          fontSize: 10,
          fontWeight: props.active ? 600 : 500,
          textAlign: "center",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {props.label}
      </span>
    </span>
  );
}

/** The shared iOS-style bottom tab bar. `active` highlights one entry. */
function TabBar(props: {
  readonly active: "inbox" | "dokumente" | "team";
}): ReactNode {
  const t = useTranslations("marketing.moduleMobilPhones");
  return (
    <div
      style={{
        flexShrink: 0,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 2,
        padding: "6px 10px 22px",
        borderTop: `1px solid ${ld("#EFF0F2", "#2c2f33")}`,
        background: ld("#fff", "#181b1f"),
      }}
    >
      <TabItem
        active={props.active === "inbox"}
        icon={
          <InboxIcon
            size={20}
            strokeWidth={props.active === "inbox" ? 2 : 1.8}
          />
        }
        label={t("inbox")}
      />
      <TabItem
        icon={<SquareCheckBig size={20} strokeWidth={1.8} />}
        label={t("aufgaben")}
      />
      <TabItem
        active={props.active === "dokumente"}
        icon={
          <Folder
            size={20}
            strokeWidth={props.active === "dokumente" ? 2 : 1.8}
          />
        }
        label={t("docs")}
      />
      <TabItem
        active={props.active === "team"}
        icon={
          <MessagesSquare
            size={20}
            strokeWidth={props.active === "team" ? 2 : 1.8}
          />
        }
        label={t("team")}
      />
      <TabItem
        icon={<Ellipsis size={20} strokeWidth={1.8} />}
        label={t("mehr")}
      />
    </div>
  );
}

/** The round verdigris floating action button (bottom-right). */
function Fab(props: { readonly icon: ReactNode }): ReactNode {
  return (
    <span
      style={{
        position: "absolute",
        right: 14,
        bottom: 68,
        width: 46,
        height: 46,
        borderRadius: "50%",
        background: H.accent,
        color: ld("#fff", "#181b1f"),
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: `0 12px 22px -6px ${ld("rgba(11,15,25,0.42)", "rgba(0,0,0,0.68)")}`,
        zIndex: 6,
      }}
    >
      {props.icon}
    </span>
  );
}

/** PHONE 1 — Inbox conversation list. */
export function PhoneInbox(): ReactNode {
  const t = useTranslations("marketing.moduleMobilPhones");
  const tData = useTranslations("marketing.moduleMobilData");
  return (
    <div
      style={{
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* search row */}
      <div
        style={{
          flexShrink: 0,
          padding: "5px 13px 11px",
          background: ld("#fff", "#181b1f"),
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span style={{ color: H.ink, display: "inline-flex" }}>
          <Menu size={21} strokeWidth={2} />
        </span>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: ld("#F4F4F2", "#292c30"),
            border: `1px solid ${ld("#E8E8E4", "#34373b")}`,
            borderRadius: 10,
            padding: "7px 10px",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              fontSize: 12,
              fontWeight: 600,
              color: H.ink,
              paddingRight: 8,
              borderRight: `1px solid ${ld("#DEDED9", "#3c3f43")}`,
              flexShrink: 0,
            }}
          >
            {t("toDo")}
            <ChevronDown size={13} strokeWidth={2.2} />
          </span>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 12.5,
              color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)"),
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {t("konversationenSuchen")}
          </span>
        </div>
      </div>
      {/* list */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          background: ld("#fff", "#181b1f"),
        }}
      >
        {mobilInbox(tData).map((r: MobilInboxRow) => (
          <div
            key={r.name}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 11,
              padding: "11px 14px",
              borderBottom: `1px solid ${ld("#F2F3F5", "#2a2d31")}`,
            }}
          >
            <span
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                background: r.bg,
                color: r.fg,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {r.initial}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: H.ink,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {r.name}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: ld("rgba(11,15,25,0.55)", "rgba(250,250,250,0.55)"),
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  marginTop: 2,
                }}
              >
                {r.sub}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 6,
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontSize: 10.5,
                  color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
                  whiteSpace: "nowrap",
                }}
              >
                {r.time}
              </span>
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  background: H.orange,
                  color: ld("#fff", "#181b1f"),
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 9,
                  fontWeight: 700,
                }}
              >
                {t("sh")}
              </span>
            </div>
          </div>
        ))}
      </div>
      <Fab icon={<SquarePen size={19} strokeWidth={2} />} />
      <TabBar active="inbox" />
    </div>
  );
}

/** A single document thumbnail (text-page or table skeleton). */
function DocThumb(props: { readonly doc: MobilDocCard }): ReactNode {
  const t = useTranslations("marketing.moduleMobilPhones");
  return (
    <div
      style={{
        position: "relative",
        height: 116,
        background: ld("#F0F1F4", "#2b2e32"),
        overflow: "hidden",
      }}
    >
      {props.doc.isText ? (
        <div
          style={{
            position: "absolute",
            top: 9,
            left: 11,
            right: 11,
            height: 175,
            background: ld("#fff", "#181b1f"),
            border: `1px solid ${ld("#E6E8EB", "#33373b")}`,
            borderRadius: "3px 3px 0 0",
            boxShadow: `0 3px 8px -6px ${ld("rgba(11,15,25,0.2)", "rgba(0,0,0,0.35)")}`,
            padding: 9,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: 3.5,
          }}
        >
          <div
            style={{
              width: "55%",
              height: 5,
              borderRadius: 2,
              background: ld("#C3C8CF", "#505357"),
            }}
          />
          <div
            style={{
              width: "100%",
              height: 2.5,
              borderRadius: 2,
              background: ld("#E9EBEE", "#313438"),
              marginTop: 3,
            }}
          />
          <div
            style={{
              width: "96%",
              height: 2.5,
              borderRadius: 2,
              background: ld("#E9EBEE", "#313438"),
            }}
          />
          <div
            style={{
              width: "92%",
              height: 2.5,
              borderRadius: 2,
              background: ld("#E9EBEE", "#313438"),
            }}
          />
          <div
            style={{
              width: "98%",
              height: 2.5,
              borderRadius: 2,
              background: ld("#E9EBEE", "#313438"),
            }}
          />
          <div
            style={{
              width: "88%",
              height: 2.5,
              borderRadius: 2,
              background: ld("#E9EBEE", "#313438"),
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 4,
            }}
          >
            <div
              style={{
                width: "40%",
                height: 2.5,
                borderRadius: 2,
                background: ld("#E9EBEE", "#313438"),
              }}
            />
            <div
              style={{
                width: "20%",
                height: 2.5,
                borderRadius: 2,
                background: ld("#D7DBE0", "#3f4246"),
              }}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div
              style={{
                width: "44%",
                height: 2.5,
                borderRadius: 2,
                background: ld("#E9EBEE", "#313438"),
              }}
            />
            <div
              style={{
                width: "18%",
                height: 2.5,
                borderRadius: 2,
                background: ld("#D7DBE0", "#3f4246"),
              }}
            />
          </div>
        </div>
      ) : null}
      {props.doc.isTable ? (
        <div
          style={{
            position: "absolute",
            top: 9,
            left: 11,
            right: 11,
            height: 175,
            background: ld("#fff", "#181b1f"),
            border: `1px solid ${ld("#E6E8EB", "#33373b")}`,
            borderRadius: "3px 3px 0 0",
            boxShadow: `0 3px 8px -6px ${ld("rgba(11,15,25,0.2)", "rgba(0,0,0,0.35)")}`,
            padding: 9,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              width: "50%",
              height: 5,
              borderRadius: 2,
              background: ld("#C3C8CF", "#505357"),
              marginBottom: 6,
            }}
          />
          <div style={{ display: "flex", borderBottom: `1px solid ${ld("#E3E6EA", "#35383c")}` }}>
            <div
              style={{
                flex: 2,
                height: 8,
                background: ld("#E0E4E9", "#373a3e"),
                borderRight: `1px solid ${ld("#fff", "#181b1f")}`,
              }}
            />
            <div
              style={{
                flex: 1,
                height: 8,
                background: ld("#E0E4E9", "#373a3e"),
                borderRight: `1px solid ${ld("#fff", "#181b1f")}`,
              }}
            />
            <div style={{ flex: 1, height: 8, background: ld("#E0E4E9", "#373a3e") }} />
          </div>
          {[0, 1, 2, 3, 4].map((rowIdx) => (
            <div
              key={rowIdx}
              style={{
                display: "flex",
                borderBottom: rowIdx === 4 ? undefined : `1px solid ${ld("#EEF0F2", "#2d3034")}`,
              }}
            >
              <div
                style={{ flex: 2, height: 7, borderRight: `1px solid ${ld("#F2F4F6", "#292c30")}` }}
              />
              <div
                style={{ flex: 1, height: 7, borderRight: `1px solid ${ld("#F2F4F6", "#292c30")}` }}
              />
              <div style={{ flex: 1, height: 7 }} />
            </div>
          ))}
        </div>
      ) : null}
      <span
        style={{
          position: "absolute",
          top: 8,
          right: 10,
          fontFamily: H.label,
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.03em",
          color: ld("#D8443E", "#fb8276"),
        }}
      >
        {t("pdf")}
      </span>
    </div>
  );
}

/** PHONE 2 — Dokumente grid. */
export function PhoneDocs(): ReactNode {
  const t = useTranslations("marketing.moduleMobilPhones");
  const tData = useTranslations("marketing.moduleMobilData");
  return (
    <div
      style={{
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* header */}
      <div
        style={{
          flexShrink: 0,
          padding: "5px 14px 11px",
          background: ld("#fff", "#181b1f"),
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span style={{ color: H.ink, display: "inline-flex" }}>
          <Menu size={21} strokeWidth={2} />
        </span>
        <span
          style={{ flex: 1, fontSize: 15.5, fontWeight: 700, color: H.ink }}
        >
          {t("dokumente")}
        </span>
        <span style={{ color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)"), display: "inline-flex" }}>
          <ArrowUpDown size={18} strokeWidth={1.9} />
        </span>
        <span style={{ color: H.accent, display: "inline-flex" }}>
          <LayoutGrid size={18} strokeWidth={1.9} />
        </span>
      </div>
      {/* grid */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          padding: "12px 13px 0",
          background: ld("#fff", "#181b1f"),
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2,1fr)",
            gap: 11,
          }}
        >
          {mobilDocs(tData).map((d: MobilDocCard) => (
            <div
              key={d.name}
              style={{
                border: `1px solid ${ld("#E9EBEF", "#313438")}`,
                borderRadius: 10,
                overflow: "hidden",
                background: ld("#fff", "#181b1f"),
              }}
            >
              <DocThumb doc={d} />
              <div
                style={{
                  padding: "8px 9px",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  borderTop: `1px solid ${ld("#F1F2F4", "#2b2e32")}`,
                }}
              >
                <span
                  style={{
                    color: ld("#D8443E", "#fb8276"),
                    display: "inline-flex",
                    flexShrink: 0,
                  }}
                >
                  <FileText size={13} strokeWidth={1.8} />
                </span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: H.ink,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {d.name}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Fab icon={<Plus size={22} strokeWidth={2.2} />} />
      <TabBar active="dokumente" />
    </div>
  );
}

/** PHONE 3 — Team-Chat conversation. */
export function PhoneChat(): ReactNode {
  const t = useTranslations("marketing.moduleMobilPhones");
  const tData = useTranslations("marketing.moduleMobilData");
  return (
    <div
      style={{
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* chat header */}
      <div
        style={{
          flexShrink: 0,
          padding: "5px 13px 10px",
          background: ld("#fff", "#181b1f"),
          borderBottom: `1px solid ${ld("#F0F1F2", "#2c2f33")}`,
          display: "flex",
          alignItems: "center",
          gap: 9,
        }}
      >
        <span style={{ color: H.ink, display: "inline-flex", marginLeft: -2 }}>
          <ChevronLeft size={22} strokeWidth={2.2} />
        </span>
        <span
          style={{
            width: 30,
            height: 30,
            borderRadius: 9,
            background: ld("#E6EAEF", "#323539"),
            color: ld("#5B6472", "#b2b6bb"),
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Users size={16} strokeWidth={2} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 700,
              color: H.ink,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {t("verwaltungLindenhof")}
          </div>
          <div
            style={{
              fontSize: 10.5,
              color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)"),
              whiteSpace: "nowrap",
            }}
          >
            {t("k3Mitglieder")}
          </div>
        </div>
        <span style={{ color: H.accent, display: "inline-flex" }}>
          <Phone size={18} strokeWidth={2} />
        </span>
        <span style={{ color: H.accent, display: "inline-flex" }}>
          <Video size={19} strokeWidth={2} />
        </span>
      </div>
      {/* messages */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          padding: "9px 13px 0",
          background: ld("#fff", "#181b1f"),
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {mobilChat(tData).map((m: MobilChatEntry, idx: number) =>
          m.kind === "date" ? (
            <div
              key={`d-${idx}`}
              style={{
                display: "flex",
                justifyContent: "center",
                margin: "3px 0",
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: ld("rgba(11,15,25,0.42)", "rgba(250,250,250,0.42)"),
                  background: ld("#F2F3F5", "#2a2d31"),
                  padding: "3px 11px",
                  borderRadius: 999,
                }}
              >
                {m.label}
              </span>
            </div>
          ) : (
            <div
              key={`m-${idx}`}
              style={{ display: "flex", alignItems: "flex-start", gap: 9 }}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: m.bg,
                  color: ld("#fff", "#181b1f"),
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 9.5,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {m.initial}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{ display: "flex", alignItems: "baseline", gap: 7 }}
                >
                  <span
                    style={{ fontSize: 12.5, fontWeight: 700, color: H.ink }}
                  >
                    {m.name}
                  </span>
                  <span style={{ fontSize: 10, color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)") }}>
                    {m.time}
                  </span>
                </div>
                {m.lines.map((ln, lnIdx) => (
                  <div
                    key={lnIdx}
                    style={{
                      fontSize: 13,
                      lineHeight: 1.42,
                      color: ld("#1A2230", "#e7ebf0"),
                      marginTop: 2,
                    }}
                  >
                    {ln}
                  </div>
                ))}
              </div>
            </div>
          ),
        )}
      </div>
      {/* composer — extra bottom padding clears the home indicator */}
      <div
        style={{
          flexShrink: 0,
          padding: "8px 12px 22px",
          background: ld("#fff", "#181b1f"),
          display: "flex",
          alignItems: "center",
          gap: 9,
        }}
      >
        <span
          style={{
            color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)"),
            display: "inline-flex",
            flexShrink: 0,
          }}
        >
          <Paperclip size={18} strokeWidth={1.9} />
        </span>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            background: ld("#F4F5F3", "#282b2f"),
            border: `1px solid ${ld("#E8E9E5", "#33363a")}`,
            borderRadius: 999,
            padding: "8px 13px",
          }}
        >
          <span
            style={{
              flex: 1,
              fontSize: 12.5,
              color: ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)"),
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {t("nachricht")}
          </span>
        </div>
        <span
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            background: H.accent,
            color: ld("#fff", "#181b1f"),
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <ArrowUp size={17} strokeWidth={2.4} />
        </span>
      </div>
    </div>
  );
}
